import { MarketAccessory, MarketList, MarketRow } from '@market/react';
import type { ComponentType } from 'react';
import {
  type MarketIconProps,
  MarketAmericanExpressMulticolorIcon,
  MarketArrowRotateCounterclockwiseIcon,
  MarketBankIcon,
  MarketBellIcon,
  MarketCalendarIcon,
  MarketCardIcon,
  MarketCashIcon,
  MarketCashMultipleIcon,
  MarketChartBarIcon,
  MarketCheckmarkCircleIcon,
  MarketCheckmarkIcon,
  MarketClockIcon,
  MarketDualRotatingArrowsIcon,
  MarketEmotionHappyIcon,
  MarketEnvelopeIcon,
  MarketEyeIcon,
  MarketFilterIcon,
  MarketFolderIcon,
  MarketGearIcon,
  MarketHomeIcon,
  MarketHumanIcon,
  MarketLightningBoltIcon,
  MarketLocationPinIcon,
  MarketLockOnIcon,
  MarketMagnifyingGlassIcon,
  MarketMinusIcon,
  MarketNoEntryCircleIcon,
  MarketPencilWritingIcon,
  MarketPhoneIcon,
  MarketPlusIcon,
  MarketPrintIcon,
  MarketQuestionMarkCircleIcon,
  MarketRightLeftIcon,
  MarketShieldCheckIcon,
  MarketStarIcon,
  MarketStoreIcon,
  MarketTagIcon,
  MarketTimerIcon,
  MarketTrashcanIcon,
  MarketWalletIcon,
  MarketXIcon,
} from '@squareup/market-react/icons';
import styles from './ValuePropositions.module.css';

// Icon lookup map for dynamic icon names
const ICON_MAP: Record<string, ComponentType<MarketIconProps>> = {
  'american-express-bw': MarketAmericanExpressMulticolorIcon,
  bank: MarketBankIcon,
  bell: MarketBellIcon,
  calendar: MarketCalendarIcon,
  card: MarketCardIcon,
  cash: MarketCashIcon,
  'cash-multiple': MarketCashMultipleIcon,
  'chart-bar': MarketChartBarIcon,
  checkmark: MarketCheckmarkIcon,
  'checkmark-circle': MarketCheckmarkCircleIcon,
  clock: MarketClockIcon,
  cog: MarketGearIcon,
  'credit-card': MarketCardIcon,
  dollar: MarketCashIcon,
  'dual-rotating-arrows': MarketDualRotatingArrowsIcon,
  edit: MarketPencilWritingIcon,
  'emotion-happy': MarketEmotionHappyIcon,
  envelope: MarketEnvelopeIcon,
  eye: MarketEyeIcon,
  filter: MarketFilterIcon,
  folder: MarketFolderIcon,
  gear: MarketGearIcon,
  help: MarketQuestionMarkCircleIcon,
  home: MarketHomeIcon,
  human: MarketHumanIcon,
  'lightning-bolt': MarketLightningBoltIcon,
  location: MarketLocationPinIcon,
  lock: MarketLockOnIcon,
  'lock-on': MarketLockOnIcon,
  minus: MarketMinusIcon,
  'no-entry-circle': MarketNoEntryCircleIcon,
  pencil: MarketPencilWritingIcon,
  person: MarketHumanIcon,
  phone: MarketPhoneIcon,
  plus: MarketPlusIcon,
  print: MarketPrintIcon,
  refresh: MarketArrowRotateCounterclockwiseIcon,
  'right-left': MarketRightLeftIcon,
  search: MarketMagnifyingGlassIcon,
  shield: MarketShieldCheckIcon,
  star: MarketStarIcon,
  store: MarketStoreIcon,
  tag: MarketTagIcon,
  timer: MarketTimerIcon,
  trash: MarketTrashcanIcon,
  wallet: MarketWalletIcon,
  x: MarketXIcon,
};

export type IconName = keyof typeof ICON_MAP;

export interface ValuePropositionsProps {
  readonly valueProps: ValueProposition[];
  readonly title?: string;
}

export interface ValueProposition {
  readonly label: string;
  readonly icon: IconName;
}

export const ValuePropositions = ({ valueProps, title }: ValuePropositionsProps) => (
  <section className={styles.valuePropsContainer} data-testid="value-propositions">
    {title && <h2 className={`heading-30 ${styles.valuePropsTitle}`}>{title}</h2>}
    <MarketList className={styles.valueProps}>
      {valueProps.map((valueProp) => {
        const IconComponent = ICON_MAP[valueProp.icon];
        return (
          <MarketRow key={valueProp.icon}>
            <label slot="label">{valueProp.label}</label>
            <MarketAccessory
              className={styles.valuePropIcon}
              size="image"
              slot="leading-accessory"
            >
              {IconComponent && <IconComponent aria-hidden />}
            </MarketAccessory>
          </MarketRow>
        );
      })}
    </MarketList>
  </section>
);
